import { Icon } from '@/new/components/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { COUNTING_OPTIONS } from '@/new/stores/timeline-calculator-store';

const EXPLANATIONS: Record<string, string> = {
  'uk-ew-calendar':
    'Every day counts, including weekends and bank holidays. The result is the linked date plus the interval, with no roll-over.',
  'uk-ew-clear-cpr':
    "The linked date and the deadline day are both excluded, so only the days strictly between them count. If the period is 5 days or fewer, weekends and bank holidays don't count toward it (CPR 2.8(4)). If the deadline lands on a non-business day, it rolls forward to the next business day (CPR 2.8(5)).",
  'uk-ew-business':
    'Skip weekends and England & Wales bank holidays. The linked date itself is day 0; the result always lands on a business day.',
  'us-federal-calendar':
    'Exclude the trigger day, then count every calendar day. If the last day falls on a Saturday, Sunday, or federal holiday, the deadline rolls forward to the next business day (FRCP 6(a)(1)(C)).',
  'us-federal-business':
    'Skip weekends and US federal holidays. The linked date itself is day 0; the result always lands on a business day.',
};

interface CountingReferenceProps {
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

export function CountingReference({ side = 'right', align = 'start' }: CountingReferenceProps = {}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          className="shrink-0 cursor-pointer"
          aria-label="How each counting method works"
        >
          <Icon className="lucide--info h-3 w-3 text-gray-400 hover:text-gray-700" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={8}
        className="w-96 max-w-[calc(100vw-2rem)] bg-white p-3 text-xs"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <p className="mb-2 text-sm font-medium text-gray-900">How counting methods work</p>
        <ul className="flex flex-col gap-2">
          {COUNTING_OPTIONS.map((opt) => (
            <li key={opt.key}>
              <p className="font-medium text-gray-800">{opt.label}</p>
              <p className="text-gray-600">{EXPLANATIONS[opt.key]}</p>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
